import addReadWhen from "@utils/storeUtils/addReadWhen";
import writableExtendable from "@utils/storeUtils/writableExtendable";
import timeout from "@utils/timeout";
import type {
  ILivechatMessage,
  ILivechatSession,
  LivechatReadyState,
} from "@utils/types";
import SockJS from "sockjs-client";
import {
  transformLivechatMessageToWebsocketMessage,
  transformWebsocketMessageToLivechatMessage,
} from "./transformMessage";
import type { EventTargetExtended, WebsocketMessage, WSHooks } from "./types";

const TOKEN = self.name;

declare var EventTarget: {
  prototype: EventTargetExtended;
  new (): EventTargetExtended;
};

export default class LivechatWebsocket extends EventTarget {
  ws?: WebSocket;
  hooks: WSHooks;
  readyState =
    writableExtendable<LivechatReadyState>("CLOSED").extend(addReadWhen);

  session = writableExtendable<ILivechatSession>({
    ok: false,
    contactId: "",
    wsUrl: "",
    closedAt: -1,
  }).extend(addReadWhen);

  constructor(hooks: WSHooks = {}) {
    super();
    this.hooks = hooks;

    this.session.subscribe($session => {
      if (!$session.ok) this.ws?.close();
    });

    this.startReconnectStrategy();
  }

  configureWebsocket = () => {
    const { readyState, hooks, session } = this;
    const $session = session.read();

    if ($session.ok) {
      const url = `${$session.wsUrl}?ContactId=${$session.contactId}`;

      const $readyState = readyState.read();

      if ($readyState === "OPEN" || $readyState === "CONNECTING") {
        return;
      }

      try {
        readyState.set("CONNECTING");
        this.ws = new SockJS(url);

        this.ws!.addEventListener("open", () => readyState.set("OPEN"));
        this.ws!.addEventListener("close", () => readyState.set("CLOSED"));
        this.ws!.addEventListener("message", ev => this.onMessage(ev));

        Object.keys(hooks).forEach(type =>
          this.ws!.addEventListener(type.toLowerCase(), hooks[type])
        );
      } catch (e) {
        readyState.set("CLOSED");
        console.error(e);
      }
    } else {
      this.ws?.close(1000);
    }
  };

  async startReconnectStrategy() {
    while (1) {
      try {
        await this.session.readWhen($session => $session.ok);
        this.configureWebsocket();
        await this.readyState.readWhen($readyState => $readyState === "CLOSED");
        this.ws = undefined;
        await timeout(3_000);
      } catch (e) {
        console.error(e);
        await timeout(4_000);
      }
    }
  }

  private onMessage(ev: MessageEvent) {
    try {
      const wsMessage: WebsocketMessage<"incoming"> = JSON.parse(ev.data);

      if (typeof wsMessage === "object" && "type" in wsMessage) {
        const livechatMessage =
          transformWebsocketMessageToLivechatMessage(wsMessage);

        this.dispatchEvent(
          new CustomEvent("message", {
            detail: {
              name: "receive:message",
              value: livechatMessage,
            },
          })
        );
      } else {
        this.dispatchEvent(
          new CustomEvent("error", {
            detail: { name: "error:unknownMessage", value: ev.data },
          })
        );
      }
    } catch (e) {
      console.error(e);
    }
  }

  send(message: ILivechatMessage) {
    try {
      const { session } = this;
      const wsMessage = transformLivechatMessageToWebsocketMessage(message, {
        token: TOKEN,
        contactId: session.read().contactId,
      });
      this.ws!.send(JSON.stringify(wsMessage));
    } catch (e) {
      console.error(e);
      this.dispatchEvent(
        new CustomEvent("error", {
          detail: {
            name: "error:sendingMessage",
            value: { key: message.timestamp },
          },
        })
      );
    }
  }
}
